import React, {useContext, useState} from 'react';
import {Button, Card, Col} from "react-bootstrap";
import { ResponsiveLine } from "@nivo/line";
import {toast} from "react-toastify";
import Row from "react-bootstrap/Row";
import {REGISTRATION_ROUTE} from "../../utils/consts";
import {useNavigate} from "react-router-dom";
import {Context} from "../../index";
import {ResponsiveBar} from "@nivo/bar";


const getQuarter = (month) => {
    return Math.floor((month + 3) / 3);
};

// 1) helper (place near top-level helpers)
const rand15 = () => 0.85 + Math.random() * 0.30;

const getPrintCost = (pages, ink) => {
    if (ink === 'black') {
        if (pages <= 108) return 2.15;
        return 0.85 + pages * 0.012;
    } else {
        if (pages <= 40) return 3.65;
        return 0.85 + pages * 0.07;
    }
};

const estimateSales = (bsr, type, quarter) => {
    // Approximate daily sales based on BSR (for Kindle or Paperback)
    let baseSales = 0;
    if (type === 'kindle') {
        if (bsr <= 100) baseSales = 500;
        else if (bsr <= 500) baseSales = 150;
        else if (bsr <= 1000) baseSales = 80;
        else if (bsr <= 5000) baseSales = 30;
        else if (bsr <= 10000) baseSales = 10;
        else if (bsr <= 50000) baseSales = 3;
        else baseSales = 1;
    } else {
        if (bsr <= 100) baseSales = 200;
        else if (bsr <= 500) baseSales = 80;
        else if (bsr <= 1000) baseSales = 40;
        else if (bsr <= 5000) baseSales = 15;
        else if (bsr <= 10000) baseSales = 5;
        else if (bsr <= 50000) baseSales = 2;
        else baseSales = 0.5;
    }

    // Seasonal adjustment by quarter (Q1 is weakest, Q4 is strongest)
    const seasonalFactor = {
        1: 0.85,
        2: 1.0,
        3: 1.1,
        4: 1.3,
    };

    const factor = seasonalFactor[quarter] || 1;
    const adjusted = baseSales * factor;

    return {
        day: adjusted,
        month: adjusted * 30,
        quarter: adjusted * 90,
        halfYear: adjusted * 180,
        year: adjusted * 365,
    };
};



const BsrCalcTool = () => {
    const [bsr, setBsr] = useState('10000');
    const [type, setType] = useState('paperback');
    const [quarter, setQuarter] = useState(new Date().getMonth() + 1);
    const [price, setPrice] = useState('15.99');
    const [pages, setPages] = useState('150');
    const [ink, setInk] = useState('black');
    const [salesData, setSalesData] = useState(null);
    const navigate = useNavigate();
    const { user } = useContext(Context);
    const isMobile = window.innerWidth < 980; // или useMediaQuery


    const getMonthlyEstimates = (yearlyUnits, unitPrice, royaltyPerUnit, startingQuarter) => {
        const monthFactors = [
            0.75, 0.70, 0.95, // Q1: Jan, Feb, Mar
            1.0, 1.1, 1.0,    // Q2: Apr, May, Jun
            0.9, 1.0, 1.3,    // Q3: Jul, Aug, Sep
            1.1, 1.4, 1.8     // Q4: Oct, Nov, Dec
        ];

        // Сумма всех коэффициентов нужна для нормализации
        const totalFactor = monthFactors.reduce((a, b) => a + b, 0);

        // Ежемесячные значения
        return monthFactors.map((factor, index) => {
            const normalizedUnits = yearlyUnits * (factor / totalFactor);
            return {
                month: index, // 0 - Jan, 11 - Dec
                units: normalizedUnits,
                sales: normalizedUnits * unitPrice,
                royalty: normalizedUnits * royaltyPerUnit,
            };
        });
    };

    const handleClear = () => {
        setBsr('10000');
        setType('paperback');
        setQuarter(new Date().getMonth() + 1);
        setPrice('15.99');
        setPages('150');
        setInk('black');
        setSalesData(null);
    }

    const handleEstimate = () => {
        if (bsr > 800000) {
            toast.error("Sales? Not with this.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (!bsr) {
            toast.error("Enter BSR Please", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (!price) {
            toast.error("Enter Price Please", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        if (type === "paperback" && !pages) {
            toast.error("Enter Page Count Please", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }

        const quarterNum = getQuarter(parseInt(quarter, 10) - 1);
        const base = estimateSales(parseInt(bsr, 10), type, quarterNum);

        const priceNum = parseFloat(price);
        const pagesNum = parseInt(pages, 10);

        const printCost = type === "paperback" ? getPrintCost(pagesNum, ink) : 0;

        let royaltyPerUnit = 0;
        if (type === "kindle") {
            royaltyPerUnit =
                priceNum >= 2.99 && priceNum <= 9.99
                    ? 0.7 * priceNum
                    : 0.35 * priceNum;
        } else {
            royaltyPerUnit = 0.6 * priceNum - printCost;
        }

        if (!Number.isFinite(royaltyPerUnit)) royaltyPerUnit = 0;
        if (royaltyPerUnit < 0) royaltyPerUnit = 0;

        const noiseFactor = rand15();

        const result = {
            day: base.day * noiseFactor,
            month: base.month * noiseFactor,
            quarter: base.quarter * noiseFactor,
            halfYear: base.halfYear * noiseFactor,
            year: base.year * noiseFactor,
        };

        const sales = {
            day: result.day * priceNum,
            month: result.month * priceNum,
            quarter: result.quarter * priceNum,
            halfYear: result.halfYear * priceNum,
            year: result.year * priceNum,
        };

        const royalties = {
            day: result.day * royaltyPerUnit,
            month: result.month * royaltyPerUnit,
            quarter: result.quarter * royaltyPerUnit,
            halfYear: result.halfYear * royaltyPerUnit,
            year: result.year * royaltyPerUnit,
        };

        const monthly = getMonthlyEstimates(result.year, priceNum, royaltyPerUnit, quarterNum);
        const sortedMonthly = monthly.sort((a, b) => a.month - b.month);

        setSalesData({ ...result, sales, royalties, monthly: sortedMonthly, noiseFactor });
    };

    const reversedMonthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const barData = user?.user?.id
        ? (salesData?.monthly || []).map((entry, index) => ({
            month: reversedMonthNames[index],
            Sales: entry.sales || 0,
            Royalty: entry.royalty || 0,
            Units: entry.units || 0,
        }))
        : [
            { month: "Jan", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Feb", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Mar", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Apr", Sales: 100, Royalty: 30, Units: 10 },
            { month: "May", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Jun", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Jul", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Aug", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Sep", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Oct", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Nov", Sales: 100, Royalty: 30, Units: 10 },
            { month: "Dec", Sales: 100, Royalty: 30, Units: 10 },
        ];



    const formattedData = [
        {
            id: "Sales",
            data: salesData?.monthly?.map((entry, index) => ({
                x: reversedMonthNames[index], // Название месяца в обратном порядке
                y: entry.sales,
            })),
        },
        {
            id: "Royalty",
            data: salesData?.monthly?.map((entry, index) => ({
                x: reversedMonthNames[index],
                y: entry.royalty,
            })),
        },
        {
            id: "units",
            data: salesData?.monthly?.map((entry, index) => ({
                x: reversedMonthNames[index],
                y: entry.units,
            })),
        },
    ];

    const labelMap = {
        Sales: "Sales",
        Royalty: "Royalty",
        units: "Units",
    };

    const unitMap = {
        Sales: "USD",
        Royalty: "USD",
        units: "Units",
    };

    const chartData = user?.user?.id
        ? formattedData
        : [

                {
                    id: 'Units',
                    data: [
                        { x: 'Jan', y: 10 },
                        { x: 'Feb', y: 10 },
                        { x: 'Mar', y: 10 },
                        { x: 'Apr', y: 10 },
                        { x: 'May', y: 10 },
                        { x: 'Jun', y: 10 },
                        { x: 'Jul', y: 10 },
                        { x: 'Aug', y: 10 },
                        { x: 'Sep', y: 10 },
                        { x: 'Oct', y: 10 },
                        { x: 'Nov', y: 10 },
                        { x: 'Dec', y: 10 },
                    ],
                },
                {
                    id: 'Sales',
                    data: [
                        { x: 'Jan', y: 100 },
                        { x: 'Feb', y: 100 },
                        { x: 'Mar', y: 100 },
                        { x: 'Apr', y: 100 },
                        { x: 'May', y: 100 },
                        { x: 'Jun', y: 100 },
                        { x: 'Jul', y: 100 },
                        { x: 'Aug', y: 100 },
                        { x: 'Sep', y: 100 },
                        { x: 'Oct', y: 100 },
                        { x: 'Nov', y: 100 },
                        { x: 'Dec', y: 100 },
                    ],
                },
                {
                    id: 'Royalty',
                    data: [
                        { x: 'Jan', y: 30 },
                        { x: 'Feb', y: 30 },
                        { x: 'Mar', y: 30 },
                        { x: 'Apr', y: 30 },
                        { x: 'May', y: 30 },
                        { x: 'Jun', y: 30 },
                        { x: 'Jul', y: 30 },
                        { x: 'Aug', y: 30 },
                        { x: 'Sep', y: 30 },
                        { x: 'Oct', y: 30 },
                        { x: 'Nov', y: 30 },
                        { x: 'Dec', y: 30 },
                    ],
                },


        ];




    return (

        <Card className="p-4 mt-0">
            <h1 className="mb-3 fs-5 text-muted">
                Amazon BSR Insights for Book Launches & Royalty Planning
            </h1>
            <p className="fst-italic text-muted small">
                This tool helps authors translate Best Sellers Rank into estimated sales and royalties,
                supporting smarter launch decisions, review planning, and post-launch optimization on Amazon KDP.
            </p>

            <div className="p-3 shadow-sm rounded border border-secondary bg-white mt-2">
                <div className="row g-3">
                    <div className={isMobile ? "col-6" : "col-md-2"}>
                        <label className="form-label text-muted fst-italic">Enter BSR:</label>
                        <input type="number" className="form-control form-control-sm" placeholder="Enter BSR..." value={bsr} onChange={(e) => setBsr(e.target.value)} />
                    </div>

                    <div className={isMobile ? "col-6" : "col-md-2"}>
                        <label className="form-label text-muted fst-italic">Book Type:</label>
                        <select className="form-select form-select-sm" value={type} onChange={(e) => setType(e.target.value)}>
                            <option value="kindle">Kindle</option>
                            <option value="paperback">Paperback</option>
                        </select>
                    </div>

                    <div className={isMobile ? "col-6" : "col-md-2"}>
                        <label className="form-label text-muted fst-italic">Price $:</label>
                        <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            value={price}
                            placeholder="Enter Price..."
                            onChange={(e) => setPrice(e.target.value)}
                        />
                    </div>

                    <div className={isMobile ? "col-6" : "col-md-2"}>
                        <label className="form-label text-muted fst-italic">Set Quarter:</label>
                        <select className="form-select form-select-sm" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                            <option value="1">Q1 Jan–Mar</option>
                            <option value="4">Q2 Apr–Jun</option>
                            <option value="7">Q3 Jul–Sep</option>
                            <option value="10">Q4 Oct–Dec</option>
                        </select>
                    </div>

                    {type === 'paperback' && (
                        <>
                            <div className={isMobile ? "col-6" : "col-md-2"}>
                                <label className="form-label text-muted fst-italic">Set Page Count:</label>
                                <input type="number" className="form-control form-control-sm" placeholder="Page Count..." value={pages} onChange={(e) => setPages(e.target.value)} />
                            </div>

                            <div className={isMobile ? "col-6" : "col-md-2"}>
                                <label className="form-label text-muted fst-italic">Set Ink Type:</label>
                                <select className="form-select form-select-sm" value={ink} onChange={(e) => setInk(e.target.value)}>
                                    <option value="black">B & W</option>
                                    <option value="color">Color</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>

                <div className="row mt-3">
                    <div className="col-auto">
                        <button className="btn btn-success btn-sm px-3" onClick={handleEstimate}>Get Estimate Sales</button>
                    </div>
                    <div className="col-auto">
                        <button className="btn btn-outline-success btn-sm px-3" onClick={handleClear}>Set Default</button>
                    </div>
                </div>

                {salesData && (
                    <div className="mt-4 small">
                        <h6 className="text-muted">Estimated Sales & Royalty</h6>
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                            <table className="table table-sm w-100 table-striped border fst-italic rounded">
                                <thead>
                                <tr>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Period:</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Sales in Units:</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Sales in USD:</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Royalty in USD:</th>
                                </tr>
                                </thead>
                                <tbody>
                                {['day', 'month', 'quarter', 'halfYear', 'year'].map((period) => (
                                    <tr key={period}>
                                        <td className="text-capitalize text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {period}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {salesData[period]?.toFixed(0)}{" "}
                                            {salesData[period] === 1 ? "Unit" : "Units"}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            $ {salesData.sales?.[period]?.toFixed(2)}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            $ {salesData.royalties?.[period]?.toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}



                {salesData?.monthly && user?.user?.id && (
                    <div className="mt-3 small">
                        <h6 className="text-muted">Monthly Breakdown from 1 January</h6>
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                            <table className="table table-sm table-striped border fst-italic rounded w-100">
                                <thead>
                                <tr>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Month</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Units</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Sales in USD</th>
                                    <th className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>Royalty</th>
                                </tr>
                                </thead>
                                <tbody>
                                {salesData.monthly.map((entry) => (
                                    <tr key={entry.month}>
                                        <td className="text-capitalize text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {new Date(0, entry.month).toLocaleString('default', { month: 'long' })}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {user?.user?.id
                                                ? `${entry.units.toFixed(0)} ${entry.units.toFixed(0) === "1" ? "Unit" : "Units"}`
                                                : '*** Units'}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {user?.user?.id ? `$ ${entry.sales.toFixed(2)}` : '$ ***'}
                                        </td>
                                        <td className="text-muted fst-italic" style={{ whiteSpace: 'nowrap' }}>
                                            {user?.user?.id ? `$ ${entry.royalty.toFixed(2)}` : '$ ***'}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {salesData?.monthly && !user?.user?.id && (
                    <Row className="text-center  mb-3">
                    <Col>

                        <Button
                            variant="success"
                            size="lg"
                            className="shadow-sm mt-3 mb-4"
                            style={{ borderRadius: '5px' }}
                            onClick={() => navigate(REGISTRATION_ROUTE)}
                        >
                            Join for Free to Unlock Full Year Forecast
                        </Button>

                        <Col xs={12} md={8} className="mx-auto">
                            <Card className="shadow-sm border">
                                <Card.Body className="p-3 p-md-4">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                    <div className="fw-semibold text-muted">
                                        Unlock Full Yearly Stats & the Chart
                                    </div>

                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="shadow-sm"
                                        onClick={() => navigate(REGISTRATION_ROUTE)}
                                    >
                                        Join for Free
                                    </Button>
                                </div>

                                <div className="rounded overflow-hidden border bg-white">
                                    <img
                                        src="/img/tools/bsr-calculator/full-statistics-bsr-calculator.png"
                                        alt="Full statistics preview: yearly totals, monthly breakdown, and chart for Amazon BSR Calculator"
                                        width={1490}
                                        height={710}
                                        className="w-100 h-auto d-block"
                                        loading="lazy"
                                    />
                                </div>

                                <div className="text-muted small mt-2">
                                    Preview of the full report available to registered users: yearly totals, monthly breakdown, and chart.
                                </div>
                            </Card.Body>
                        </Card>
                        </Col>



                    </Col>
                </Row>
            )}
                {salesData?.monthly && user?.user?.id && (
                    <div style={{ width: "100%", overflowX: "auto" }}>
                        <h6 className="text-muted d-none mt-3 d-md-block">
                            Monthly Sales, Royalties & Units Overview (From January)
                        </h6>
                        <h6 className="text-muted d-md-none">
                            Monthly Sales Overview
                        </h6>
                        <div
                            style={{
                                height: 400,
                                width: window.innerWidth < 576 ? 600 : "100%", // шире на мобилках для скролла
                                minWidth: 550,
                            }}
                        >
                            <ResponsiveBar
                                data={barData}
                                keys={["Sales", "Royalty", "Units"]}
                                indexBy="month"
                                groupMode="grouped"
                                margin={{
                                    top: 10,
                                    right: window.innerWidth < 576 ? 20 : 8,
                                    bottom: window.innerWidth < 576 ? 50 : 80,
                                    left: window.innerWidth < 576 ? 40 : 60,
                                }}
                                padding={0.25}
                                innerPadding={4}
                                valueScale={{ type: "linear" }}
                                indexScale={{ type: "band", round: true }}
                                colors={{ scheme: "set2" }}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: "Value (USD / Units)",
                                    legendOffset: -50,
                                    legendPosition: "middle",
                                    format: (v) => Math.round(v),
                                }}
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: window.innerWidth < 576 ? -30 : -45,
                                    legend: "Month",
                                    legendOffset: 60,
                                    legendPosition: "middle",
                                }}
                                enableGridY={true}
                                enableLabel={false}
                                tooltip={({ id, value, indexValue }) => (
                                    <div
                                        style={{
                                            background: "white",
                                            padding: "6px 9px",
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <strong>{indexValue}</strong>
                                        <br />
                                        {id}: {Math.round(value)} {id === "Units" ? "Units" : "USD"}
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>

        </Card>
    );
};

export default BsrCalcTool;
